import cookie from "@/utils/cookies";
import { getStore, removeStore, setStore } from "@/utils/storage";

const STORAGE_PREFIX = "diagnosis_guest_verify_";
const LAST_PHONE_PREFIX = "diagnosis_guest_last_phone_";
const INDEX_PREFIX = "diagnosis_guest_verify_index_";
const PROFILE_PREFIX = "diagnosis_guest_profile_";
const USER_PROFILE_PREFIX = "diagnosis_user_profile_";
const userProfileKey = (uid) => `${USER_PROFILE_PREFIX}${shopKey() || "default"}_${uid}`;
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const shopKey = () => {
  try {
    return String(cookie.getInClient("shopId") || "");
  } catch (e) {
    return "";
  }
};

const normalizePhone = (phone) => {
  const p = String(phone || "").trim();
  return /^1\d{10}$/.test(p) ? p : "";
};

const tokenStorageKeys = (phone) => {
  const p = normalizePhone(phone);
  if (!p) return [];
  const sid = shopKey();
  return [`${STORAGE_PREFIX}${sid}_${p}`, `${STORAGE_PREFIX}phone_only_${p}`];
};

const lastPhoneKeys = () => {
  const sid = shopKey() || "default";
  return [`${LAST_PHONE_PREFIX}${sid}`, `${LAST_PHONE_PREFIX}default`, `${LAST_PHONE_PREFIX}phone_only`];
};

const indexKey = () => `${INDEX_PREFIX}${shopKey() || "default"}`;

const readIndex = () => {
  try {
    const raw = getStore(indexKey());
    if (!raw) return [];
    const list = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(list) ? list.map(String) : [];
  } catch (e) {
    return [];
  }
};

const writeIndex = (phones) => {
  const uniq = [...new Set((phones || []).map(normalizePhone).filter(Boolean))];
  setStore(indexKey(), uniq);
  setStore(`${INDEX_PREFIX}phone_only`, uniq);
};

/**
 * 记住最近使用的诊断手机号（返回首页回填）
 */
export const rememberDiagnosisPhone = (phone) => {
  if (!process.client) return;
  const p = normalizePhone(phone);
  if (!p) return;
  lastPhoneKeys().forEach((key) => setStore(key, p));
};

export const getRememberedDiagnosisPhone = () => {
  if (!process.client) return "";
  for (const key of lastPhoneKeys()) {
    const p = normalizePhone(getStore(key));
    if (p) return p;
  }
  return "";
};

/**
 * @returns {{ token: string, expireAt: number } | null}
 */
export const getDiagnosisVerifyToken = (phone) => {
  if (!process.client) return null;
  const p = normalizePhone(phone);
  if (!p) return null;
  try {
    for (const key of tokenStorageKeys(p)) {
      const raw = getStore(key);
      if (!raw) continue;
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!data || !data.token || !data.expireAt) continue;
      if (Date.now() > Number(data.expireAt)) {
        removeStore(key);
        continue;
      }
      return { token: String(data.token), expireAt: Number(data.expireAt) };
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const hasValidDiagnosisVerifyToken = (phone) => !!getDiagnosisVerifyToken(phone);

/** 当前环境下仍有效的已验证手机号列表 */
export const listVerifiedDiagnosisPhones = () => {
  if (!process.client) return [];
  const fromIndex = readIndex();
  const remembered = getRememberedDiagnosisPhone();
  const candidates = [...fromIndex];
  if (remembered) candidates.push(remembered);
  // 兜底：扫描 localStorage
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
      const m = key.match(/(\d{11})$/);
      if (m) candidates.push(m[1]);
    }
  } catch (e) {
    /* ignore */
  }
  return [...new Set(candidates)]
    .map(normalizePhone)
    .filter((p) => p && hasValidDiagnosisVerifyToken(p));
};

export const getLastDiagnosisVerifiedPhone = () => {
  const verified = listVerifiedDiagnosisPhones();
  if (!verified.length) return "";
  const remembered = getRememberedDiagnosisPhone();
  if (remembered && verified.includes(remembered)) return remembered;
  return verified[0];
};

export const saveDiagnosisVerifyToken = (phone, token, expireTime) => {
  if (!process.client || !token) return;
  const p = normalizePhone(phone);
  if (!p) return;
  let expireAt = Date.now() + DEFAULT_TTL_MS;
  if (expireTime) {
    // 兼容 "yyyy-MM-dd HH:mm:ss"
    const normalized = String(expireTime).replace(/-/g, "/");
    const t = new Date(normalized).getTime();
    if (!Number.isNaN(t) && t > Date.now()) {
      expireAt = t;
    }
  }
  const payload = { token: String(token), expireAt, phone: p };
  tokenStorageKeys(p).forEach((key) => setStore(key, payload));
  rememberDiagnosisPhone(p);
  const idx = readIndex();
  if (!idx.includes(p)) {
    writeIndex([...idx, p]);
  } else {
    writeIndex(idx);
  }
  console.info(
    "[diagnosis] saved verifyToken for",
    p,
    "expireAt=",
    new Date(expireAt).toLocaleString()
  );
};

export const clearDiagnosisVerifyToken = (phone) => {
  if (!process.client) return;
  const p = normalizePhone(phone);
  if (!p) return;
  tokenStorageKeys(p).forEach((key) => removeStore(key));
  writeIndex(readIndex().filter((x) => x !== p));
  clearDiagnosisProfile(phone);
};

export const isDiagnosisVerifyExpiredMsg = (msg) => {
  const s = String(msg || "");
  return (
    s.includes("验证已失效") ||
    s.includes("短信验证码") ||
    s.includes("手机号和短信") ||
    /请.*验证码/.test(s)
  );
};

const profileStorageKeys = (phone) => {
  const p = normalizePhone(phone);
  if (!p) return [];
  const sid = shopKey();
  return [`${PROFILE_PREFIX}${sid}_${p}`, `${PROFILE_PREFIX}phone_only_${p}`];
};

/** 保存/覆盖游客档案（仅在 user/save 验证通过后调用；绝不触碰凭证记录） */
export const saveDiagnosisProfile = (phone, profile) => {
  if (!process.client || !profile || typeof profile !== "object") return;
  const p = normalizePhone(phone);
  if (!p) return;
  const payload = { ...profile, phone: p };
  profileStorageKeys(p).forEach((key) => setStore(key, payload));
};

/** 读取游客档案：凭证过期/不存在时删除档案并返回 null（档案跟着凭证有效期走） */
export const getDiagnosisProfile = (phone) => {
  if (!process.client) return null;
  const p = normalizePhone(phone);
  if (!p) return null;
  if (!hasValidDiagnosisVerifyToken(p)) {
    clearDiagnosisProfile(p); // 凭证过期：只物理删除这条手机号的档案
    return null;
  }
  try {
    for (const key of profileStorageKeys(p)) {
      const raw = getStore(key);
      if (!raw) continue;
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (data && typeof data === "object") return data;
    }
  } catch (e) {
    /* ignore */
  }
  return null;
};

/** 删除指定手机号的档案 */
export const clearDiagnosisProfile = (phone) => {
  if (!process.client) return;
  profileStorageKeys(phone).forEach((key) => removeStore(key));
};

/* ==================== 已登录用户诊断档案（key 前缀与游客不同，物理隔离） ==================== */
/** 保存/覆盖登录用户诊断档案（key 用 uid，天然区分多账号） */
export const saveDiagnosisUserProfile = (uid, phone, profile) => {
  if (!process.client || !profile || typeof profile !== "object") return;
  const id = String(uid || "");
  if (!id) return;
  setStore(userProfileKey(id), { ...profile, uid: id, phone: String(phone || "") });
};

/** 读取登录用户诊断档案：uid + phone 双重校验，防止脱敏手机号首尾一致串号 */
export const getDiagnosisUserProfile = (uid, phone) => {
  if (!process.client) return null;
  const id = String(uid || "");
  if (!id) return null;
  try {
    const raw = getStore(userProfileKey(id));
    if (!raw) return null;
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!data || String(data.uid) !== id) return null;
    if (String(data.phone || "") !== String(phone || "")) return null;
    return data;
  } catch (e) {
    return null;
  }
};

export const clearDiagnosisUserProfile = (uid) => {
  if (!process.client) return;
  const id = String(uid || "");
  if (!id) return;
  removeStore(userProfileKey(id));
};